import { useEffect, useState } from 'react';
import { voiceScript } from '@elena/shared';
import { ElenaAvatar } from '../components/ElenaAvatar';
import type { WaveMode } from '../components/Waveform';

/**
 * #/avatar: only the 3D Elena, for the phone app's WebView. Needs no token (no API calls).
 * The app drives her with postMessage JSON:
 *   { type: 'line', text, durationMs }  she says this line (lips, mood, gesture)
 *   { type: 'stop' }                    she stops talking
 *   { type: 'mode', mode }              idle | listening | thinking | speaking
 * Options: #/avatar?view=head for a face close-up.
 */
type Message =
  | { type: 'line'; text: string; durationMs?: number | null }
  | { type: 'stop' }
  | { type: 'mode'; mode: WaveMode };

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

export function AvatarEmbed() {
  const [mode, setMode] = useState<WaveMode>('idle');
  const compact = new URLSearchParams(location.hash.split('?')[1] ?? '').get('view') === 'head';

  useEffect(() => {
    document.documentElement.classList.add('is-embed');
    const onMessage = (event: Event) => {
      const raw = (event as MessageEvent).data;
      let msg: Message;
      try {
        msg = typeof raw === 'string' ? (JSON.parse(raw) as Message) : (raw as Message);
      } catch {
        return;
      }
      if (msg?.type === 'line' && typeof msg.text === 'string') {
        voiceScript.start(msg.text, msg.durationMs ?? null);
        setMode('speaking');
      } else if (msg?.type === 'stop') {
        voiceScript.stop();
        setMode('idle');
      } else if (msg?.type === 'mode') {
        setMode(msg.mode);
      }
    };
    // Android's WebView delivers on document, iOS and browsers on window
    window.addEventListener('message', onMessage);
    document.addEventListener('message', onMessage);
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ready' }));
    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <main className="avatar-embed">
      <ElenaAvatar mode={mode} size={compact ? 'compact' : 'hero'} />
    </main>
  );
}
